import { CartLineNode, ShopifyCartItem } from '@/lib/shopify';
import {
  convertCartLineItemPropertiesToAttributes,
  getCartLineItemProperty,
} from '@/utils/shopify';
import { CartItemProperty } from '@/core/constants';
import { normalizeToBoolean } from '@/utils/strings';
import { convertRubyHashToJson } from './rubyHashToJson';

export type SerializedCartItemBase = {
  id: string;
  title: string;
  handle: string;
  imageUrl: string;
  price: string;
  currency: string;
  quantity: number;
  subtotal: string;
  subtotalCurrency: string;
  variantTitle: string;
  variantId: string;
  isAddon: boolean;
  maximumQuantity?: number;
  savings?: string;
};

export type SerializedCartItem = SerializedCartItemBase & {
  addons: SerializedCartItemBase[];
};

/**
 * Creates a Map from attributes array for O(1) lookups
 * @param attributes - Array of attribute objects
 * @returns Map with key-value pairs
 */
function createAttributesMap(
  attributes: Array<{ key: string; value: string }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const attr of attributes) {
    map.set(attr.key, attr.value);
  }
  return map;
}

/**
 * Creates a getProperty function that checks both regular and underscore-prefixed keys
 * @param attributesMap - Map of attributes
 * @returns Function that returns the value for a key (checking both key and _key)
 */
function createGetPropertyFunction(
  attributesMap: Map<string, string>,
): (key: string) => string | undefined {
  return (key: string): string | undefined => {
    return attributesMap.get(key) || attributesMap.get(`_${key}`);
  };
}

/**
 * Determines the maximum quantity for a cart item based on product handle and quantity limitation
 * @param handle - The product handle
 * @param quantityLimitedToOne - Whether the item is quantity limited to one
 * @param parentAttributes - The parent cart item attributes to check for subscription
 * @returns The maximum quantity (1 or undefined)
 */
function determineMaximumQuantity(
  handle: string,
  quantityLimitedToOne: string | undefined,
  parentAttributes: Array<{ key: string; value: string }> = [],
): number | undefined {
  // Array of handle patterns that should be limited to one quantity
  const quantityLimitedHandles = [
    'play-kits',
    'subscription-1',
    'course',
    'subscription-gifting',
  ];

  const isQuantityLimitedToOne = quantityLimitedToOne === 'true';
  const isHandleLimited = quantityLimitedHandles.some((pattern) =>
    handle.includes(pattern),
  );

  // Check if this is a book bundle with a subscription parent
  const isBookBundle = handle.includes('book-bundle');
  const isBookBundleWithSubscriptionParent =
    isBookBundle &&
    parentAttributes.some(
      (attr) =>
        attr.key === 'is_playgenius_subscription' && attr.value === 'true',
    );

  if (
    isHandleLimited ||
    isQuantityLimitedToOne ||
    isBookBundleWithSubscriptionParent
  ) {
    return 1;
  }

  return undefined;
}

/**
 * Creates a SerializedCartItemBase from a CartLineNode
 * @param node - The cart line node
 * @param itemPrice - The calculated display price
 * @param parentAttributes - The parent cart item attributes (for addons)
 * @returns SerializedCartItemBase
 */
function createSerializedCartItemBase({
  node,
  itemPrice,
  isAddon,
  parentAttributes,
}: {
  node: CartLineNode;
  itemPrice: { price: string; currency: string };
  isAddon: boolean;
  parentAttributes?: Array<{ key: string; value: string }>;
}): SerializedCartItemBase {
  const attributes = node.attributes || [];
  const attributesMap = createAttributesMap(attributes);
  const getProperty = createGetPropertyFunction(attributesMap);
  const quantityLimitedToOne = getProperty('quantity_limited_to_one');
  const handle = node.merchandise.product?.handle || '';

  const unitPrice = parseFloat(itemPrice.price);
  const subtotal = (unitPrice * node.quantity).toFixed(2);

  return {
    id: node.id,
    title: node.merchandise.product?.title || '',
    handle,
    imageUrl: node.merchandise.image?.url || '',
    price: itemPrice.price,
    currency: itemPrice.currency,
    quantity: node.quantity,
    subtotal,
    subtotalCurrency: node.cost.subtotalAmount.currencyCode,
    variantTitle: node.merchandise.title || '',
    variantId: node.merchandise.id || '',
    maximumQuantity: determineMaximumQuantity(
      handle,
      quantityLimitedToOne,
      parentAttributes || [],
    ),
    isAddon,
  };
}

/**
 * Calculate the display price for a cart item based on complex business logic
 * This replicates the liquid template price logic
 */
export function calculateDisplayPrice(node: CartLineNode): {
  price: string;
  currency: string;
  savings?: string;
} {
  const attributes = node.attributes || [];
  const productHandle = node.merchandise.product?.handle;
  const currencyCode = node.merchandise.price?.currencyCode || 'USD';

  // Create attributes map for O(1) lookups
  const attributesMap = createAttributesMap(attributes);

  // Extract properties from attributes
  const getProperty = createGetPropertyFunction(attributesMap);

  // Check item types
  const isBookBundle = getProperty('is_book_bundle') === 'true';
  const priceSource = getProperty('price_source');
  const displayPrice = getProperty('display_price');
  const price = getProperty('price');

  // Subscription bundle handles
  const playgeniusPlayKitBundle = 'subscription-1';
  const playgeniusGiftBundle = 'subscription-gifting';

  // Handle subscription payload for Play Kit Bundle
  if (productHandle === playgeniusPlayKitBundle) {
    const subscriptionsPayload = getProperty('subscriptionsPayload');

    if (subscriptionsPayload) {
      try {
        // Convert Ruby hash syntax to valid JSON
        const jsonString = convertRubyHashToJson(subscriptionsPayload);

        const payload = JSON.parse(jsonString);

        const bundleDiscountedPrice = payload.bundleDiscountedPrice;

        if (
          bundleDiscountedPrice !== undefined &&
          bundleDiscountedPrice !== null
        ) {
          const calculatedPrice = (bundleDiscountedPrice / 100).toFixed(2);
          return {
            price: calculatedPrice, // Convert from cents
            currency: currencyCode,
            savings:
              payload.bundle?.savings !== undefined &&
              payload.bundle?.savings !== null
                ? (payload.bundle.savings / 100).toFixed(2)
                : undefined,
          };
        }
      } catch (e) {
        console.warn('Failed to parse subscriptionsPayload:', e);
      }
    }
  }

  // Handle book bundles
  if (isBookBundle && price) {
    return {
      price: (Number(price) / 100).toFixed(2),
      currency: currencyCode,
    };
  }

  // Handle gift bundles
  if (productHandle === playgeniusGiftBundle) {
    const gifts = getProperty('gifts');

    if (gifts) {
      try {
        // Convert Ruby hash syntax to valid JSON
        const jsonString = convertRubyHashToJson(gifts);
        const giftsData = JSON.parse(jsonString);
        const bundlePrice = giftsData.bundle?.price;
        if (bundlePrice) {
          return {
            price: (parseInt(bundlePrice) / 100).toFixed(2), // Convert from cents
            currency: currencyCode,
          };
        }
      } catch (e) {
        console.warn('Failed to parse gifts data:', e);
      }
    }
  }

  // Handle items with price_source and display_price
  if (priceSource && displayPrice) {
    const unitPrice = parseFloat(displayPrice).toFixed(2);
    return {
      price: unitPrice,
      currency: currencyCode,
    };
  }

  // Default: use individual item price, fallback to subtotal if individual price is missing
  const individualPrice = node.merchandise.price?.amount;
  const fallbackPrice =
    individualPrice || String(node.cost.subtotalAmount.amount);

  return {
    price: String(fallbackPrice),
    currency:
      node.merchandise.price?.currencyCode ||
      node.cost.subtotalAmount.currencyCode,
  };
}

enum CartProductType {
  PlayKitSubscription = 'Play Kit Subscription',
  PlayKitSubscriptionBookBundleAddon = 'Play Kit Subscription Book Bundle Addon',
  PlayKitGiftSubscription = 'Play Kit Gift Subscription',
  SubscriptionPlan = 'Subscription Plan',
  CoursePack = 'Course Pack',
  Standalone = 'Standalone',
}

export function organizeCartItems(items: ShopifyCartItem[]) {
  type CartItemGroups = {
    playKitSubscriptionProduct: ShopifyCartItem | null;
    playKitSubscriptionBookBundleAddonProduct: ShopifyCartItem | null;
    playKitGiftSubscriptionProduct: ShopifyCartItem | null;
    subscriptionPlanProducts: ShopifyCartItem[];
    coursePackProducts: ShopifyCartItem[];
    standaloneProducts: ShopifyCartItem[];
  };

  const initialGroups: CartItemGroups = {
    playKitSubscriptionProduct: null,
    playKitSubscriptionBookBundleAddonProduct: null,
    playKitGiftSubscriptionProduct: null,
    subscriptionPlanProducts: [],
    coursePackProducts: [],
    standaloneProducts: [],
  };

  return items.reduce((groups, item) => {
    const productType = identifyProductType(item);

    switch (productType) {
      case CartProductType.PlayKitSubscription:
        groups.playKitSubscriptionProduct = item;
        break;

      case CartProductType.PlayKitSubscriptionBookBundleAddon:
        groups.playKitSubscriptionBookBundleAddonProduct = item;
        break;

      case CartProductType.PlayKitGiftSubscription:
        groups.playKitGiftSubscriptionProduct = item;
        break;

      case CartProductType.SubscriptionPlan:
        groups.subscriptionPlanProducts.push(item);
        break;

      case CartProductType.CoursePack:
        groups.coursePackProducts.push(item);
        break;

      case CartProductType.Standalone:
        groups.standaloneProducts.push(item);
        break;
    }

    return groups;
  }, initialGroups);
}

export function identifyProductType(item: ShopifyCartItem): CartProductType {
  const attributes = convertCartLineItemPropertiesToAttributes(item.properties);

  const conditions: [boolean, CartProductType][] = [
    [
      !!getCartLineItemProperty(
        CartItemProperty.PlayKitSubscriptionSettings,
        attributes,
      ),
      CartProductType.PlayKitSubscription,
    ],
    [
      !!getCartLineItemProperty(
        CartItemProperty.PlayKitGiftSubscriptionSettings,
        attributes,
      ),
      CartProductType.PlayKitGiftSubscription,
    ],
    [
      !!getCartLineItemProperty(
        CartItemProperty.SubscriptionPlanSettings,
        attributes,
      ),
      CartProductType.SubscriptionPlan,
    ],
    [
      normalizeToBoolean(
        getCartLineItemProperty(
          CartItemProperty.IsPlayKitSubscriptionBookBundleAddon,
          attributes,
        ),
      ),
      CartProductType.PlayKitSubscriptionBookBundleAddon,
    ],
    [
      normalizeToBoolean(
        getCartLineItemProperty(CartItemProperty.IsCoursePack, attributes),
      ),
      CartProductType.CoursePack,
    ],
  ];

  for (const [identifyingCondition, productType] of conditions) {
    if (identifyingCondition) {
      return productType;
    }
  }

  return CartProductType.Standalone;
}

/**
 * Determines if a potential addon node is actually an addon for the given parent node
 */
export function isAddonForParent(
  potentialAddon: CartLineNode,
  parentNode: CartLineNode,
  _allEdges: { node: CartLineNode }[],
): boolean {
  if (potentialAddon.id === parentNode.id) return false; // Skip self

  const addonAttributes = potentialAddon.attributes || [];
  const addonTitle = potentialAddon.merchandise.product?.title || '';
  const parentAttributes = parentNode.attributes || [];

  // Create attributes maps for O(1) lookups
  const addonAttributesMap = createAttributesMap(addonAttributes);
  const parentAttributesMap = createAttributesMap(parentAttributes);

  // Create getProperty functions for centralized attribute lookup
  const getAddonProperty = createGetPropertyFunction(addonAttributesMap);
  const getParentProperty = createGetPropertyFunction(parentAttributesMap);

  // Priority 1: Check for explicit parent_product_id attribute (highest priority)
  const parentProductId = getAddonProperty('parent_product_id');
  if (parentProductId) {
    // If parent_product_id exists, it must match exactly or return false
    return parentProductId === parentNode.merchandise.id;
  }

  // Priority 2: Only use legacy logic if no parent_product_id exists
  const isCoursePack = getParentProperty('is_course_pack') === 'true';
  const isSubscription =
    getParentProperty('is_playgenius_subscription') === 'true';

  if (!isCoursePack && !isSubscription) return false;

  if (isCoursePack) {
    // For course packs, look for book bundles with linked_product_handle matching parent handle
    const linkedProductHandle = getAddonProperty('linked_product_handle');
    const isBookBundle =
      addonTitle.includes('Book Bundle') ||
      potentialAddon.merchandise.product?.handle?.includes('book-bundle');

    return (
      !!linkedProductHandle &&
      linkedProductHandle === parentNode.merchandise.product?.handle &&
      Boolean(isBookBundle)
    );
  }

  if (isSubscription) {
    // For subscriptions, look for subscription addons
    const isSubscriptionAddon =
      getAddonProperty('type') === 'Subscription Add-On';
    const isBookBundle = getAddonProperty('is_book_bundle') === 'true';

    return isSubscriptionAddon || isBookBundle;
  }

  return false;
}

/**
 * Serializes cart line edges into a friendly array of cart item objects with addons grouped.
 * @param {Array<{ node: CartLineNode }>} edges - The cart line edges from the cart response.
 * @returns {Array<SerializedCartItem>} - Friendly cart items with addons.
 */
export function serializeCartLines(
  edges: { node: CartLineNode }[],
): SerializedCartItem[] {
  // Create a Map for O(1) node lookups by ID
  const nodeMap = new Map<string, CartLineNode>();
  edges.forEach(({ node }) => {
    nodeMap.set(node.id, node);
  });

  const addonItems = new Set<string>();
  const parentToAddons = new Map<string, string[]>();
  const assignedAddons = new Set<string>(); // Track which addons have been assigned

  // Pre-process attributes for all nodes to avoid repeated find() operations
  const nodeAttributesMap = new Map<string, Map<string, string>>();
  edges.forEach(({ node }) => {
    const attributes = node.attributes || [];
    nodeAttributesMap.set(node.id, createAttributesMap(attributes));
  });

  // Pre-process parent references to eliminate O(n²) operations
  const parentReferences = new Map<string, Set<string>>();
  const legacyReferences = new Map<string, Set<string>>();

  edges.forEach(({ node }) => {
    const attributesMap = nodeAttributesMap.get(node.id);
    if (!attributesMap) return;

    const getProperty = createGetPropertyFunction(attributesMap);
    const parentProductId = getProperty('parent_product_id');
    const linkedProductHandle = getProperty('linked_product_handle');

    if (parentProductId) {
      if (!parentReferences.has(parentProductId)) {
        parentReferences.set(parentProductId, new Set());
      }
      const parentRef = parentReferences.get(parentProductId);
      if (parentRef) {
        parentRef.add(node.id);
      }
    }

    if (linkedProductHandle) {
      if (!legacyReferences.has(linkedProductHandle)) {
        legacyReferences.set(linkedProductHandle, new Set());
      }
      const legacyRef = legacyReferences.get(linkedProductHandle);
      if (legacyRef) {
        legacyRef.add(node.id);
      }
    }
  });

  // Identify potential parents and link their addons
  edges.forEach(({ node }) => {
    const attributesMap = nodeAttributesMap.get(node.id);
    if (!attributesMap) return;

    // Check if this is a potential parent (has addons)
    const getProperty = createGetPropertyFunction(attributesMap);
    const isCoursePack = getProperty('is_course_pack') === 'true';
    const isSubscription = getProperty('is_playgenius_subscription') === 'true';

    // O(1) lookup instead of O(n) some() operations
    const hasExplicitParentReference = parentReferences.has(
      node.merchandise.id,
    );
    const hasLegacyParentReference = legacyReferences.has(
      node.merchandise.product?.handle || '',
    );

    // Use explicit parent reference as primary, fallback to legacy methods
    const hasAddons = hasExplicitParentReference || hasLegacyParentReference;

    if (isCoursePack || isSubscription || hasAddons) {
      // Find all potential addons for this parent
      edges.forEach(({ node: potentialAddon }) => {
        // Skip if this addon has already been assigned to another parent
        if (assignedAddons.has(potentialAddon.id)) return;

        if (isAddonForParent(potentialAddon, node, edges)) {
          addonItems.add(potentialAddon.id);
          assignedAddons.add(potentialAddon.id); // Mark as assigned
          if (!parentToAddons.has(node.id)) {
            parentToAddons.set(node.id, []);
          }
          const addons = parentToAddons.get(node.id);
          if (addons) {
            addons.push(potentialAddon.id);
          }
        }
      });
    }
  });

  // Create the serialized items with addons
  const result: SerializedCartItem[] = [];

  for (const { node } of edges) {
    // Skip addon items from main list
    if (addonItems.has(node.id)) continue;

    const addonIds = parentToAddons.get(node.id) || [];
    const addons = [];

    // Process addons
    for (const addonId of addonIds) {
      const addonNode = nodeMap.get(addonId);
      if (!addonNode) continue;

      const addonPrice = calculateDisplayPrice(addonNode);
      addons.push(
        createSerializedCartItemBase({
          node: addonNode,
          itemPrice: addonPrice,
          isAddon: true,
          parentAttributes: node.attributes || [],
        }),
      );
    }

    const itemPrice = calculateDisplayPrice(node);

    result.push({
      ...createSerializedCartItemBase({
        node,
        itemPrice,
        isAddon: false,
      }),
      addons,
      savings: itemPrice.savings,
    });
  }

  return result;
}
